// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@awc/App/Components/Notify";
import SubmitCartRfqForm from "@awc/App/Handlers/CustomForms/CartRfqFormHandler";
import { CartRfqApiParameter } from "@awc/App/Services/CustomForms";
import { xIsAuthenticated } from "@awc/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@awc/App/Store/Selectors/SettingsSelectors";
import { xGoogleAnalyticsState } from "@awc/App/Widgets/GoogleAnalyticsConnector/Store/Reducers/xGoogleAnalytics";
import { trackHubspotRfqGa4Events } from "@awc/App/Widgets/GoogleAnalyticsConnector/Utilities/DataLayer";
import { applyMask, getUtmParams, mergeWithCss, useValidate, xDefaultRules } from "@awc/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import loadBillTo from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/LoadBillTo";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, CountryModel } from "@insite/client-framework/Types/ApiModels";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import FileUpload from "@insite/mobius/FileUpload";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC, RefObject, useContext, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { AnyStyledComponent } from "styled-components";

interface OwnProps {
    cartLines: CartLineModel[];
    setModalOpen: (value: boolean) => void;
    extendedStyles?: CartRfqFormSyles;
    onlyQuoteRequired?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    countries: getCurrentCountries(state),
    currentBillTo: getCurrentBillToState(state).value,
    session: state.context.session,
    reCaptchaEnabledForCartRfq: getSettingsCollection(state).xngSecuritySettings.checkReCaptchaForCartRfqOnClientSide,
    isAuth: xIsAuthenticated(state),
    requestQuoteEvent: (state as xGoogleAnalyticsState).xGoogleAnalytics.requestQuoteEvent,
});

const mapDispatchToProps = {
    SubmitCartRfqForm,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
    loadCurrentCountries,
    loadBillTo,
    resetCart: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Data/Carts/Reset",
        });
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface CartRfqFormSyles {
    formWrapper?: InjectableCss;
    inputsWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    fileUpload?: InjectableCss;
}

export const cartRfqFormStyles: CartRfqFormSyles = {};

const StyledForm = getStyledWrapper("form");

const CartRfqForm: FC<Props> = ({
    SubmitCartRfqForm,
    loadCurrentCountries,
    loadBillTo,
    countries,
    reCaptchaEnabledForCartRfq,
    extendedStyles,
    session,
    currentBillTo,
    isAuth,
    cartLines,
    setModalOpen,
    resetCart,
    validateReCaptcha,
    requestQuoteEvent,
    onlyQuoteRequired,
}) => {
    const [styles] = useState(() => mergeWithCss(cartRfqFormStyles, extendedStyles));
    const [errorMessage, setErrorMessage] = useState("");
    const [country, setCountry] = useState<CountryModel>();
    const [file, setFile] = useState<any>(null);

    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();

    const toaster = useContext(ToasterContext);

    const { masksList, setFieldValue, values, field, handleSubmit, isSubmitting, errors } = useValidate({
        initialValues: {
            firstName: session.firstName || "",
            lastName: session.lastName || "",
            company: "",
            phone: "",
            email: session.email || "",
            countryCode: "",
            city: "",
            state: "",
            postalCode: "",
            notes: "",
            attachmentBase64: "",
            attachmentFileName: "",
            attachmentMimeType: "",
        },
        validators: {
            firstName: `required|${xDefaultRules.firstName}`,
            lastName: `required|${xDefaultRules.lastName}`,
            company: `required|${xDefaultRules.companyName}`,
            phone: `required|${xDefaultRules.phone}`,
            email: `required|${xDefaultRules.email}`,
            countryCode: `required|${xDefaultRules.city}`,
            city: `required|${xDefaultRules.city}`,
            state: country?.states?.length ? `required|${xDefaultRules.text}` : `${xDefaultRules.text}`,
            postalCode: `${
                country?.abbreviation?.toLowerCase() === "ca"
                    ? "postalCodeCA"
                    : country?.abbreviation?.toLowerCase() === "us"
                      ? "postalCodeUS"
                      : "text"
            }`,
            notes: xDefaultRules.notes,
        },
        onSubmit: (fieldValues: CartRfqApiParameter, { setSubmitting }: any) => {
            submitHandler(fieldValues, setSubmitting);
        },
    });

    // Load countries if not available and set selected country
    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        } else if (isAuth && currentBillTo) {
            // Set Selected Country
            const selectedCountry = countries?.find(c => c.abbreviation === currentBillTo?.country?.abbreviation);
            setCountry(selectedCountry);
            selectedCountry && setFieldValue("countryCode", selectedCountry?.abbreviation);

            // Set Selected State
            const selectedState = selectedCountry?.states?.find(
                c => c.abbreviation === currentBillTo?.state?.abbreviation,
            );
            selectedState && setFieldValue("state", selectedState?.abbreviation);
        }
    }, [countries, currentBillTo]);

    // Load billing info if missing and set form fields
    useEffect(() => {
        if (!isAuth) {
            return;
        }

        if (!currentBillTo) {
            loadBillTo();
        } else {
            setFieldValue("company", currentBillTo?.companyName);
            setFieldValue("phone", currentBillTo?.phone);
            setFieldValue("city", currentBillTo.city);
            setFieldValue("postalCode", currentBillTo.postalCode);
        }
    }, [currentBillTo]);

    const submitHandler = async (fieldValues: CartRfqApiParameter, setSubmitting: any) => {
        setSubmitting(true);
        if (Object.keys(errors).length > 0) {
            setSubmitting(false);
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CartRfq", recaptchaRef });

        if (!isReCaptchaValid) {
            setSubmitting(false);
            return;
        }
        const cartModal = {
            cart: {
                cartLines,
            },
        };

        const utmParameters = getUtmParams();

        SubmitCartRfqForm({
            ...fieldValues,
            ...(utmParameters ? { utmParameters, formUrl: window.location.href } : {}),
            cart: cartModal.cart as any,
            onlyQuoteRequired,
            onSuccess: () => {
                toaster.addToast({
                    body: translate("Request for quote submitted, items have been removed from your cart."),
                    messageType: "success",
                });
                setSubmitting(false);
                resetCart();
                setModalOpen(false);
                setErrorMessage("");
                requestQuoteEvent && trackHubspotRfqGa4Events();
            },
            onError: (error: string) => {
                setSubmitting(false);
                setErrorMessage(error);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    this.onSuccess?.(resultProps.apiResult.result);
                } else if (resultProps.apiResult?.errorMessage) {
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    const handleUploadFileChange = (e: ChangeEvent<AnyStyledComponent>) => {
        const reader = new FileReader();
        const attachmentFile = e.target.files?.[0];
        setFile(attachmentFile);
        reader.onload = () => {
            const base64DataUrl = reader.result as string;
            const base64Data = base64DataUrl?.split(",")[1];

            setFieldValue("attachmentBase64", base64Data);
            setFieldValue("attachmentFileName", attachmentFile.name);
            setFieldValue("attachmentMimeType", attachmentFile.type);
        };
        reader.readAsDataURL(attachmentFile);
    };

    return (
        <StyledForm onSubmit={handleSubmit} noValidate data-test-selector="cartRfqFrom">
            <StyledWrapper {...styles.formWrapper}>
                <StyledWrapper {...styles.inputsWrapper}>
                    <TextField
                        {...field("firstName")}
                        label={translate("First name")}
                        required={true}
                        {...{ displayStarForRequired: true }}
                        error={field("firstName").error && "First name is a required field"}
                    />

                    <TextField
                        {...field("lastName")}
                        label={translate("Last Name")}
                        required={true}
                        {...{ displayStarForRequired: true }}
                        error={field("lastName").error && "Last name is a required field"}
                    />

                    <TextField
                        {...field("email")}
                        type="email"
                        label={translate("Email")}
                        required={true}
                        {...{ displayStarForRequired: true }}
                        error={field("email").error && "Email is a required field"}
                    />

                    <TextField
                        {...field("company")}
                        label={translate("Company name")}
                        error={field("company").error && "Company name is a required field"}
                        required={true}
                        {...{ displayStarForRequired: true }}
                    />

                    <TextField
                        {...field("phone")}
                        type="phone"
                        label={translate("Phone number")}
                        error={field("phone").error && "Phone number is a required field"}
                        required={true}
                        {...{ displayStarForRequired: true }}
                    />

                    {countries && countries.length > 1 && (
                        <Select
                            {...field("countryCode")}
                            label={translate("Country")}
                            onChange={(e: any) => {
                                const key = e.target.name;
                                const oldValue = values[key];
                                const newValue = e.target.value;
                                const masked = applyMask(masksList[key], newValue, oldValue);
                                setFieldValue(key, masked);
                                // XNG-Changes
                                const selectedCountry = countries?.find(c => c.abbreviation === masked);
                                setCountry(selectedCountry);
                                if (!selectedCountry?.states?.length) {
                                    setFieldValue("state", "");
                                } else if (selectedCountry.states.length === 1) {
                                    setFieldValue("state", selectedCountry.states[0].abbreviation);
                                }
                            }}
                            error={field("countryCode").error && "Country is a required field"}
                            required={true}
                            {...{ displayStarForRequired: true }}
                        >
                            <option value="">{translate("Select Country")}</option>
                            {countries?.map((o: any) => (
                                <option key={o.abbreviation} value={o.abbreviation}>
                                    {o.name}
                                </option>
                            ))}
                        </Select>
                    )}

                    {country && (
                        <TextField
                            {...field("city")}
                            label={translate("City")}
                            required={true}
                            {...{ displayStarForRequired: true }}
                            error={field("city").error && "City is a required field"}
                        />
                    )}

                    {country && (
                        <TextField
                            {...field("postalCode")}
                            label={translate("Postal Code")}
                            type={
                                country?.abbreviation?.toLowerCase() === "ca"
                                    ? "postalCodeCA"
                                    : country?.abbreviation?.toLowerCase() === "us"
                                      ? "postalCodeUS"
                                      : "text"
                            }
                            error={field("postalCode").error && "Postal code is a required field"}
                        />
                    )}

                    {country && country?.states && !!country.states?.length && (
                        <Select
                            {...field("state")}
                            label={translate("State/Region")}
                            error={field("state").error && "State/Region is a required field"}
                            required={true}
                            {...{ displayStarForRequired: true }}
                        >
                            <option value="" disabled>
                                {translate("Select State")}
                            </option>
                            {country.states.map((o: any) => (
                                <option key={o.abbreviation} value={o.abbreviation}>
                                    {o.name}
                                </option>
                            ))}
                        </Select>
                    )}

                    <TextField
                        {...field("notes")}
                        label={translate("Notes")}
                        error={field("notes").error && ""}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.fileUpload}>
                    <FileUpload
                        onFileChange={e => {
                            handleUploadFileChange(e);
                        }}
                        inputRef={fileUploadRef}
                        fileName={file?.name || ""}
                        labelPosition="top"
                        uid="CartRfq"
                        buttonText={translate("Choose File")}
                        label={translate("Upload Attachment")}
                        buttonProps={{
                            variant: "tertiary",
                        }}
                    />
                </StyledWrapper>
                <ReCaptcha
                    recaptchaRef={recaptchaRef}
                    location="CartRfq"
                    {...{ forceEnable: reCaptchaEnabledForCartRfq }}
                    {...{ displayForAuthenticated: true }}
                />
                <Button
                    type="submit"
                    disabled={isSubmitting}
                    {...styles.submitButton}
                    data-test-selector="submit_cart_rfq_form"
                >
                    {translate("Request For Quote")}
                </Button>
                {errorMessage && (
                    <Notify type="danger" data-test-selector="cartRfqFrom_ErrorMessage">
                        {errorMessage}
                    </Notify>
                )}
            </StyledWrapper>
        </StyledForm>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(CartRfqForm);
